import React, { useState, useEffect, useRef } from "react";
import { useModal } from "@Contexts/ModalContext";
import Button from "@UI/kit/Button/Button";
import Input from "@UI/kit/Input/Input";
import { ReactComponent as ArrowLeft } from "@Icons/payments/ArrowLeft.svg";
import { useTranslation } from "react-i18next";
import { wafaRequest, wafaConfirm, wafaResend, mpesaRequest } from "../../api/axiosRequest";
import Loader from "../../UI/kit/Loader/Loader";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

const PhoneVerification = () => {
  const navigate = useNavigate();
  const { content, closeModal, openModal } = useModal();
  const { t } = useTranslation();
  const { plateNumber, parkingUid, phone: initialPhone, provider = "wafa" } = content;

  // Определяем код страны и длину номера в зависимости от провайдера
  const countryCode = provider === "mpesa" ? "+254" : "+212";
  const phoneLength = provider === "mpesa" ? 13 : 14; // +254 123456789 (13) vs +212 123456789 (14)

  const [step, setStep] = useState("phoneVerification");
  const [phone, setPhone] = useState(initialPhone || `${countryCode} `);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [referenceId, setReferenceID] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const firstInputRef = useRef(null);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (step === "verificationCode" && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [step]);

  const handlePhoneChange = (event) => {
    const input = event.target.value;

    if (!input.startsWith(countryCode)) {
      setPhone(`${countryCode} `);
    } else {
      const filteredPhone = input.replace(/[^\d\s]/g, "");
      const codeLength = countryCode.length - 1; // Remove '+'
      const numberPart = filteredPhone.slice(codeLength + 1).replace(/\s/g, "");
      const maxDigits = provider === "mpesa" ? 9 : 9; // Both use 9 digits after country code
      if (numberPart.length <= maxDigits) {
        setPhone(`${countryCode} ${numberPart}`);
      }
    }
  };

  const handlePhoneSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    if (provider === "mpesa") {
      // M-Pesa: STK Push с ожиданием подтверждения
      const formattedPhone = phone.replace(/\D/g, "");
      await mpesaRequest(plateNumber, parkingUid, formattedPhone)
        .then((res) => {
          closeModal();
          // Открываем модалку ожидания с опросом статуса
          openModal("paymentPending", {
            statusUrl: res.statusUrl,
            plateNumber,
            parkingUid
          });
        })
        .finally(() => setIsLoading(false));
    } else {
      // Wafa: с OTP подтверждением
      const formattedPhone = phone.replace(/\D/g, "").replace(/^212/, "");
      await wafaRequest(plateNumber, parkingUid, `212${formattedPhone}`)
        .then((res) => {
          setReferenceID(res?.referenceId);
          setStep("verificationCode");
          setResendTimer(59);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleOtpChange = (value, index) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    const otpCode = otp.join("");

    setIsLoading(true);
    await wafaConfirm(referenceId, otpCode)
      .then((res) => {
        closeModal();
        // navigate(`/payment-success/${parkingUid}/${plateNumber}`);
        window.location.href = `${process.env.REACT_APP_BASE_URL}/payment-success/${parkingUid}/${plateNumber}`;
      })
      .finally(() => setIsLoading(false));
  };

  const resendCode = async () => {
    setIsResending(true);
    setResendTimer(59);

    const formattedPhone = phone.replace(/\D/g, "").replace(/^212/, "");

    await wafaResend(`212${formattedPhone}`)
      .then((res) => {})
      .finally(() => {
        setIsResending(false);
      });
  };

  return (
    <div className="flex h-[90vh] flex-col space-y-8 sm:h-full">
      <div className="relative flex justify-center py-4">
        <button
          onClick={() => {
            if (step === "phoneVerification") {
              closeModal();
            } else {
              setStep("phoneVerification");
            }
          }}
          type="button"
        >
          <ArrowLeft className="absolute left-2 top-4" />
        </button>
        <p>{t("phone_verification")}</p>
      </div>

      {step === "phoneVerification" && (
        <form onSubmit={handlePhoneSubmit} className="flex w-full justify-center pb-8">
          <div className="flex w-full max-w-[390px] flex-col items-center space-y-[14px]">
            <Input
              type="text"
              label={t("your_phone")}
              onChange={handlePhoneChange}
              value={phone}
              placeholder={provider === "mpesa" ? "+254..." : "+212..."}
            />
            {isLoading ? (
              <Loader />
            ) : (
              <Button
                type="submit"
                disabled={phone.length < phoneLength}
                text={provider === "mpesa" ? t("send_payment_request") : t("send_me_verification_code")}
                styles="bg-btnBlue hover:bg-btnBlueHover max-w-[390px] flex items-center justify-center"
              />
            )}
          </div>
        </form>
      )}

      {step === "verificationCode" && (
        <form onSubmit={handleOtpSubmit} className="flex flex-col items-center space-y-6 pb-8">
          <p className="max-w-[180px] text-center text-sm">{t("enter_the_code_from_sms")}</p>
          <div className="flex space-x-2">
            {otp.map((value, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                value={value}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                ref={index === 0 ? firstInputRef : null}
                className="h-12 w-12 rounded-lg border p-2 text-center text-lg"
                maxLength={1}
              />
            ))}
          </div>
          {isLoading ? (
            <Loader />
          ) : (
            <Button type="submit" text={t("check_and_pay")} disabled={otp.some((val) => !val)} />
          )}
          <button
            type="button"
            onClick={resendCode}
            className={clsx("text-sm text-link", (resendTimer > 0 || isResending) && "opacity-40")}
            disabled={resendTimer > 0 || isResending}
          >
            {resendTimer > 0 ? `${t("resend_code")} (${resendTimer})` : t("resend_code")}
          </button>
        </form>
      )}
    </div>
  );
};

export default PhoneVerification;
